import React, { useState, useEffect } from 'react';
import { LockClosedIcon, PlayIcon, ClockIcon, SparklesIcon } from '@heroicons/react/24/solid';
import Button from '../ui/Button';
import Card from '../ui/Card';
import VideoPlayer from '../media/VideoPlayer';
import ImageCarousel from '../media/ImageCarousel';
import AgeGate from './AgeGate';

interface PostPreviewProps {
  title?: string;
  content: string;
  contentType: 'TEXT' | 'IMAGE' | 'VIDEO' | 'AUDIO' | 'POLL' | 'LINK';
  mediaUrls?: string[];
  isPublic: boolean;
  isEarlyAccess?: boolean;
  earlyAccessHours?: number;
  publishedAt?: string;
  ageRestricted?: boolean;
  tierIds?: string[];
  teaserText?: string;
  previewLength?: number;
  onSubscribe?: () => void;
  onUnlock?: () => void;
  className?: string;
}

/**
 * Post Preview Component
 * Shows teaser/preview of locked content with subscribe/unlock CTA
 */
const PostPreview: React.FC<PostPreviewProps> = ({
  title,
  content,
  contentType,
  mediaUrls = [],
  isPublic,
  isEarlyAccess = false,
  earlyAccessHours = 0,
  publishedAt,
  ageRestricted = false,
  tierIds = [],
  teaserText,
  previewLength = 150,
  onSubscribe,
  onUnlock,
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAgeGate, setShowAgeGate] = useState(false);
  const [ageVerified, setAgeVerified] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<string | null>(null);

  // Calculate early access time remaining
  useEffect(() => {
    if (isEarlyAccess && earlyAccessHours && publishedAt) {
      const updateTimeRemaining = () => {
        const publishDate = new Date(publishedAt);
        const earlyAccessEndDate = new Date(publishDate.getTime() + earlyAccessHours * 60 * 60 * 1000);
        const now = new Date();
        const diff = earlyAccessEndDate.getTime() - now.getTime();

        if (diff <= 0) {
          setTimeRemaining(null);
          return;
        }

        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        
        if (hours > 24) {
          const days = Math.floor(hours / 24);
          setTimeRemaining(`${days}d ${hours % 24}h`);
        } else if (hours > 0) {
          setTimeRemaining(`${hours}h ${minutes}m`);
        } else {
          setTimeRemaining(`${minutes}m`);
        }
      };

      updateTimeRemaining();
      const interval = setInterval(updateTimeRemaining, 60000); // Update every minute

      return () => clearInterval(interval);
    }
    // Ensure consistent return type for all code paths
    return undefined;
  }, [isEarlyAccess, earlyAccessHours, publishedAt]);

  // Generate preview text
  const getPreviewText = () => {
    if (teaserText) return teaserText;
    
    if (contentType === 'TEXT' || contentType === 'LINK') {
      if (content.length <= previewLength) return content;
      return content.substring(0, previewLength) + '...';
    }
    
    return '';
  };

  const previewText = getPreviewText();
  const isLocked = !isPublic || ageRestricted || (isEarlyAccess && tierIds.length > 0);

  return (
    <Card className={`post-preview ${className} ${isLocked ? 'opacity-75' : ''}`}>
      <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
        {isEarlyAccess && timeRemaining && (
          <div className="bg-purple-600/90 backdrop-blur-sm text-white px-3 py-1 rounded-full flex items-center gap-1.5 text-sm font-medium shadow-lg">
            <SparklesIcon className="w-4 h-4" />
            <span>Early Access</span>
            <span className="text-purple-200">•</span>
            <ClockIcon className="w-3.5 h-3.5" />
            <span>{timeRemaining}</span>
          </div>
        )}
        {isLocked && (
          <div className="bg-patreon-orange/90 backdrop-blur-sm text-white px-3 py-1 rounded-full flex items-center gap-1 text-sm font-medium shadow-lg">
            <LockClosedIcon className="w-4 h-4" />
            <span>Patron Only</span>
          </div>
        )}
      </div>

      <div className={`relative ${isLocked ? 'blur-sm' : ''}`}>
        {title && (
          <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
        )}

        {/* Media Preview */}
        {mediaUrls && mediaUrls.length > 0 && (
          <div className="mb-4">
            {contentType === 'IMAGE' && (
              <>
                {mediaUrls.length === 1 ? (
                  /* Single Image */
                  <div className="relative aspect-video bg-gray-200 rounded-lg overflow-hidden">
                    <img
                      src={mediaUrls[0]}
                      alt={title || 'Post image'}
                      className="w-full h-full object-cover"
                      style={{ filter: isLocked ? 'blur(20px)' : 'none' }}
                    />
                    {isLocked && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                        <LockClosedIcon className="w-12 h-12 text-white" />
                      </div>
                    )}
                  </div>
                ) : (
                  /* Multiple Images - Carousel */
                  <div className="relative">
                    {isLocked ? (
                      <div className="relative aspect-video bg-gray-200 rounded-lg overflow-hidden">
                        <img
                          src={mediaUrls[0]}
                          alt={title || 'Post image'}
                          className="w-full h-full object-cover blur-xl"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                          <div className="text-center text-white">
                            <LockClosedIcon className="w-12 h-12 mx-auto mb-2" />
                            <p className="text-sm">{mediaUrls.length} images</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <ImageCarousel
                        images={mediaUrls}
                        alt={title || 'Post images'}
                        showThumbnails={true}
                        allowFullscreen={true}
                      />
                    )}
                  </div>
                )}
              </>
            )}

            {contentType === 'VIDEO' && mediaUrls[0] && (
              <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                {isLocked ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <LockClosedIcon className="w-16 h-16 text-white" />
                    <PlayIcon className="w-12 h-12 text-white/50 absolute" />
                  </div>
                ) : ageRestricted && !ageVerified ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                    <div className="text-center text-white">
                      <LockClosedIcon className="w-16 h-16 mx-auto mb-4" />
                      <p className="text-lg font-semibold mb-4">Age-restricted content</p>
                      <Button
                        onClick={() => setShowAgeGate(true)}
                        className="bg-patreon-orange text-white hover:bg-patreon-orange/90"
                      >
                        Verify Age
                      </Button>
                    </div>
                  </div>
                ) : (
                  <VideoPlayer
                    src={mediaUrls[0]}
                    title={title}
                    controls={true}
                    className="w-full h-full"
                  />
                )}
              </div>
            )}

            {contentType === 'AUDIO' && mediaUrls[0] && (
              <div className="bg-gray-100 rounded-lg p-4">
                {isLocked ? (
                  <div className="flex items-center justify-center gap-2 text-gray-500">
                    <LockClosedIcon className="w-5 h-5" />
                    <span>Audio content locked</span>
                  </div>
                ) : (
                  <audio src={mediaUrls[0]} controls className="w-full">
                    Your browser does not support the audio tag.
                  </audio>
                )}
              </div>
            )}
          </div>
        )}

        {/* Text Preview */}
        {(contentType === 'TEXT' || contentType === 'LINK') && previewText && (
          <div className="mb-4">
            <p className="text-gray-700 leading-relaxed">
              {isExpanded ? content : previewText}
              {!isExpanded && content.length > previewLength && (
                <button
                  onClick={() => setIsExpanded(true)}
                  className="text-patreon-orange hover:underline ml-1"
                >
                  Read more
                </button>
              )}
            </p>
          </div>
        )}

        {/* Content Type Indicator */}
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
          {isEarlyAccess && (
            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
              Early Access
            </span>
          )}
          {ageRestricted && (
            <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full">
              18+
            </span>
          )}
        </div>
      </div>

      {/* Unlock CTA */}
      {isLocked && (
        <div className="mt-6 p-6 bg-gradient-to-r from-patreon-orange/10 to-patreon-pink/10 rounded-lg border-2 border-patreon-orange/20">
          <div className="text-center">
            <LockClosedIcon className="w-12 h-12 text-patreon-orange mx-auto mb-3" />
            <h4 className="text-lg font-bold text-gray-900 mb-2">
              This content is for patrons only
            </h4>
            <p className="text-gray-600 mb-4">
              {isEarlyAccess 
                ? 'Become a patron to get early access to this content'
                : ageRestricted
                ? 'This content is age-restricted. Please verify your age to continue.'
                : 'Subscribe to unlock this exclusive content'
              }
            </p>
            <div className="flex gap-3 justify-center">
              {onSubscribe && (
                <Button
                  onClick={onSubscribe}
                  className="bg-patreon-orange text-white hover:bg-patreon-orange/90"
                >
                  Become a Patron
                </Button>
              )}
              {onUnlock && (
                <Button
                  onClick={onUnlock}
                  variant="outline"
                  className="border-patreon-orange text-patreon-orange hover:bg-patreon-orange/10"
                >
                  Unlock Content
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Age Gate Modal */}
      {ageRestricted && showAgeGate && (
        <AgeGate
          minAge={18}
          isOpen={showAgeGate}
          onClose={() => setShowAgeGate(false)}
          onVerify={(verified) => {
            setAgeVerified(verified);
            setShowAgeGate(false);
          }}
          contentTitle={title || 'this content'}
        />
      )}
    </Card>
  );
};

export default PostPreview;


